
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {CURSOR_MARKER_COLOR} from "../CursorPointMarker.js";
import {DigitizationTools} from "../ToolRegistry.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {HeightAuxiliaryVisual} from "../HeightAuxiliaryVisual.js";
import {createFeature} from "../Feature.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "../ScreenSpaceScale.js";
import {createMarker} from "./perpendicularCapture.js";
import {computeHeightTriangle, formatSlopeLabel, heightSummaryLabelAnchor} from "../geometry/measurementGeometry.js";
import {formatLength} from "./distanceTool.js";

import {t, T} from "../i18n/index.js";
const HELPER_COLOR = 0x00e5ff; // matches the Midtpunkt/Vinkelret/Parallelforskydning family's own baseline/helper convention

// Mål højde: fixed 2-point capture (same "throwaway single-point preview
// marker, second click commits" shape as midpointTool.js's own
// runMidpointCapture - see that file's doc comment for why this family
// uses customCapture rather than the generic maxPoints flow), but UNLIKE
// Midtpunkt, both captured points ARE the feature's own real geometry (a
// 2-point LineString - not replaced by a derived point). The visible
// auxiliary right triangle (horizontal/vertical edges, the two non-90
// angles) is purely decorative, built by HeightAuxiliaryVisual.js and
// recomputed on every geometry_changed (drag) - task's own "værdierne
// opdateres dynamisk hvis brugeren flytter vertix".
export const HeightTool = {
	id: "digitize_height",
	name: T("Mål højde"),
	category: "measurement",
	kind: "measurement",
	toolbarGroup: "measurement",
	toolbarOrder: 20,
	icon: "measure_height.svg",
	resultGeometryType: "LineString",
	tooltip: T("Mål højdeforskellen mellem to punkter, med vandret afstand og hældningsvinkel."),
	instructions: T("Venstreklik for at vælge det første punkt, derefter det andet."),

	customCapture(dm, tool){
		runHeightCapture(dm, tool);
	},
	// Restore extension point (see DigitizationModule._buildLoadedFeature): the finished measurement keeps its labels and triangle.
	decorateLoaded(visual){ applyHeightRepresentation(visual); },
};

DigitizationTools.register(HeightTool);

// The finished height measurement, shared by the capture flow and the restore hook (decorateLoaded).
// task's own Målinger contract: no vertex coordinates, a distance label on the one real segment (the direct A-B "slope" distance -
// horizontal/vertical/angle come from the auxiliary triangle, which is NOT a captured segment). The triangle is recomputed on every
// geometry_changed (drag) so the values follow the vertices.
function applyHeightRepresentation(visual){
	visual.setVertexLabelsSuppressed(true);
	visual.enableSegmentLabels((p, q) => formatLength(p.distanceTo(q)));
	visual.enableSummaryLabel(
		(raw, v) => {
			if(raw.length < 2) return null;
			const tri = computeHeightTriangle(raw[0], raw[1]); // ikke "t": det ville skygge for i18n-funktionen t()
			return [
				v.name,
				t("Højde: {0}", formatLength(tri.verticalDistance)),
				t("Vandret afstand: {0}", formatLength(tri.horizontalDistance)),
				t("Skrå afstand: {0}", formatLength(tri.directDistance)),
				formatSlopeLabel(tri.verticalDistance, tri.horizontalDistance),
			];
		},
		(raw) => {
			// See geometry/measurementGeometry.js's heightSummaryLabelAnchor() for the placement rule: centred inside the triangle once
			// it's large enough to hold the label without crowding the auxiliary triangle's own two edge-midpoint labels, otherwise
			// extended past the "high" point (found via live testing to be the one placement that stays clear of the corner AND both
			// edge labels for a small/shallow triangle).
			const tri = computeHeightTriangle(raw[0], raw[1]);
			const [x, y, z] = heightSummaryLabelAnchor(tri);
			return {x, y, z};
		},
	);

	const auxiliary = new HeightAuxiliaryVisual();
	visual.add(auxiliary);
	visual.userData.auxiliaryVisual = auxiliary;
	const refresh = () => {
		const [p0, p1] = visual.points;
		if(p0 && p1) auxiliary.updateFromPoints(p0.position, p1.position);
	};
	refresh();
	visual.addEventListener("geometry_changed", refresh);
}

function runHeightCapture(dm, tool){
	const viewer = dm.viewer;
	const domElement = viewer.renderer.domElement;

	const markerA = createMarker(CURSOR_MARKER_COLOR);
	dm.scene.add(markerA);

	let pointA = null;
	let finished = false;

	const updateScales = () => {
		if(!markerA.visible) return;
		const camera = viewer.scene.getActiveCamera();
		const size = viewer.renderer.getSize(new THREE.Vector2());
		const s = computeScreenSpaceScale(camera, markerA.getWorldPosition(new THREE.Vector3()), camera.position, size.width, size.height, ScreenSpaceSizes.computedPoint);
		markerA.scale.set(s, s, s);
	};
	const onUpdate = () => updateScales();
	viewer.addEventListener("update", onUpdate);

	const removeConstructionGeometry = () => {
		dm.scene.remove(markerA);
	};

	const cleanup = () => {
		domElement.removeEventListener("mouseup", onMouseUp, false);
		viewer.removeEventListener("cancel_insertions", onCancel);
		viewer.removeEventListener("update", onUpdate);
		dm.clearRubberBand();
	};

	const finalize = (a, b) => {
		const visual = new FeatureVisual("LineString");
		visual.appendAtEnd(a);
		visual.appendAtEnd(b);

		applyHeightRepresentation(visual);

		const feature = createFeature({
			geometryType: "LineString",
			coordinates: visual.toCoordinates(),
			category: tool.category,
			tool: tool.id,
			featureType: tool.featureType || tool.name,
			name: `${tool.name} ${dm.featureManager.features.length + 1}`,
		});

		visual.setInfo(feature.properties.name, feature.properties.description);
		dm.scene.add(visual);

		dm._wireFeatureLifecycle(feature, visual); // visuals map, FeatureManager, snap targets, geometry_changed sync, feature_clicked

		dm.dispatchEvent({type: "feature_created", feature, visual});
	};

	// a not-yet-completed height measurement has nothing valid to keep -
	// both points are required before there is a meaningful triangle at
	// all, same reasoning as midpointTool.js's own onCancel
	const onCancel = () => {
		if(finished) return;
		finished = true;
		cleanup();
		removeConstructionGeometry();
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;
		if(e.button !== THREE.MOUSE.LEFT) return;

		const location = dm._pickPlacementPosition(e);
		if(!location) return;

		if(!pointA){
			pointA = location.clone();
			dm._markOperationStarted();
			markerA.position.copy(pointA);
			markerA.visible = true;
			dm.setRubberBand(pointA);
			dm._showInstruction(tool.name, t("Venstreklik for at vælge det andet punkt og beregne højden."));
			return;
		}

		const pointB = location.clone();

		finished = true;
		cleanup();
		removeConstructionGeometry();
		finalize(pointA, pointB);

		// stay active, matching Midtpunkt/Vinkelret/Parallelforskydning's
		// own "keep measuring without re-clicking the tool icon" behaviour
		if(dm.activeTool === tool){
			dm.startInsertion(tool);
		}
	};

	dm._showInstruction(tool.name, t("Venstreklik for at vælge det første punkt."));
	domElement.addEventListener("mouseup", onMouseUp, false);
	viewer.addEventListener("cancel_insertions", onCancel);
}
